import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { PropertyBadge } from '../../components/PropertyBadge';
import { SignalLine, PreventRepeatModal } from '../../components/ExperienceProtection';
import { LogRequestModal } from '../../components/LogRequestModal';
import { EndShiftModal } from '../../components/EndShiftModal';
import { TaskCard } from '../../components/TaskCard';
import { BriefingReportsDrawer } from '../../components/BriefingReportsDrawer';
import { NextTouchModal } from '../../components/NextTouchModal';
import { useProperty } from '../../context/PropertyContext';
import { useAuth } from '../../context/AuthContext';
import { useGuestHubArrivalToast } from '../../hooks/useGuestHubArrivalToast';
import { getProperties, getTasks, seedData, startShift, getActiveShift, updateTaskStatus, setTaskNextTouch, setTaskAssignment } from '../../lib/api';
import { 
  AlertTriangle, Clock, Calendar, Play, MessageSquare, User, CheckCircle, 
  ArrowRight, FileDown, RefreshCw, Loader2, Database, Coffee, 
  Timer, Phone, Mail, ChevronRight, MoreHorizontal, UserPlus, Send,
  CalendarClock, ArrowUpRight, Pause, Bell, Inbox, Sun, Moon, Plus, Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const DailyRun = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [shiftDate, setShiftDate] = useState('today');
  const [calmMode, setCalmMode] = useState(false);
  const [shiftStarted, setShiftStarted] = useState(false);
  const [waitingTab, setWaitingTab] = useState('guest');
  const [seedingData, setSeedingData] = useState(false);
  const [preventRepeatOpen, setPreventRepeatOpen] = useState(false);
  const [preventRepeatTask, setPreventRepeatTask] = useState(null);
  const [logRequestOpen, setLogRequestOpen] = useState(false);
  const [endShiftOpen, setEndShiftOpen] = useState(false);
  const [reportsDrawerOpen, setReportsDrawerOpen] = useState(false);
  const [nextTouchOpen, setNextTouchOpen] = useState(false);
  const [nextTouchTask, setNextTouchTask] = useState(null);
  
  const [handleNowItems, setHandleNowItems] = useState([]);
  const [dueTodayItems, setDueTodayItems] = useState([]);
  const [waitingItems, setWaitingItems] = useState([]);
  const [scheduledItems, setScheduledItems] = useState([]);
  const [carryOverItems, setCarryOverItems] = useState([]);
  const [allTasks, setAllTasks] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftDate]);

  // Poll for new GuestHub arrivals → toast when fresh requests land.
  useGuestHubArrivalToast({ propertyId: selectedProperty });

  // Date helpers — used to bucket tasks against the selected shift date
  const dateBounds = (which) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (which === 'tomorrow') start.setDate(start.getDate() + 1);
    if (which === 'yesterday') start.setDate(start.getDate() - 1);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  };
  const isWithin = (iso, { start, end }) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d >= start && d < end;
  };
  const sameDateOnly = (iso, { start }) => {
    // Compare YYYY-MM-DD when caller stored a date-only string (carry_over_from_date)
    if (!iso) return false;
    const wanted = start.toISOString().slice(0, 10);
    return String(iso).slice(0, 10) === wanted;
  };

  const loadData = async () => {
    try {
      const [propsRes, tasksRes, shiftRes] = await Promise.all([
        getProperties(),
        getTasks(),
        getActiveShift(),
      ]);
      setProperties(propsRes.data);
      const tasks = tasksRes.data || [];
      setAllTasks(tasks);
      setShiftStarted(!!shiftRes.data?.active_shift);
      
      // Map task to display format
      const mapTask = (t) => ({
        id: t.id,
        priority: t.priority === 'HANDLE_NOW' ? 'Now' : t.priority === 'DUE_TODAY' ? 'Today' : t.priority === 'WAITING_ON' ? 'Waiting' : 'Scheduled',
        propertyId: t.propertyId,
        guest: t.guest_name_text || '',
        channel: t.source_channel || '',
        summary: t.summary,
        dueAt: t.dueAt || null,
        due: t.dueAt ? format(new Date(t.dueAt), 'h:mm a') : '',
        timing: t.dueAt ? format(new Date(t.dueAt), 'h:mm a') : '',
        nextStep: t.notes || '',
        owner: t.assigned_to_user_id || null,
        route_to: t.route_to || null,
        status: t.status === 'NEW' ? 'New' : t.status === 'IN_PROGRESS' ? 'In progress' : t.status === 'WAITING' ? 'Waiting' : 'Done',
        type: t.category?.replace(/_/g, ' '),
        signal_type: t.signal_type,
        impact_area: t.impact_area,
        fix_route: t.fix_route,
        prevent_repeat_note: t.prevent_repeat_note,
        prevent_repeat_action: t.prevent_repeat_action,
        waitingOn: t.waiting_on ? (t.waiting_on === 'GUEST' ? 'guest' : t.waiting_on === 'VENDOR' ? 'vendor' : 'property') : null,
        last_updated_at: t.last_updated_at || t.last_touched_at,
        lastTouch: t.last_touched_at ? `Updated ${format(new Date(t.last_touched_at), 'h:mm a')}` : '',
        carry_over_reason: t.carry_over_reason,
        carry_over_from_date: t.carry_over_from_date,
        closed_at: t.closed_at,
        next_touch_at: t.next_touch_at,
        next_touch_reason: t.next_touch_reason,
        waiting_on: t.waiting_on,
        _raw: t,
      });

      const bounds = dateBounds(shiftDate);
      const active = tasks.filter(t => t.status !== 'DONE' && !t.closed_at);

      if (shiftDate === 'today') {
        // Today: live shift view — Handle Now, Due Today, Waiting On (due today),
        // Scheduled (due today), and Carry-Over flagged for today.
        setHandleNowItems(active.filter(t => t.priority === 'HANDLE_NOW').map(mapTask));
        setDueTodayItems(active.filter(t => t.priority === 'DUE_TODAY' && !t.carry_over_from_date).map(mapTask));
        setWaitingItems(active.filter(t => (t.status === 'WAITING' || t.priority === 'WAITING_ON') && (!t.dueAt || isWithin(t.dueAt, bounds))).map(mapTask));
        setScheduledItems(active.filter(t => t.priority === 'SCHEDULED').map(mapTask));
        setCarryOverItems(active.filter(t => t.carry_over_from_date).map(mapTask));
      } else if (shiftDate === 'tomorrow') {
        // Tomorrow: only tasks scheduled for tomorrow + carry-overs with next_touch tomorrow.
        setHandleNowItems([]);
        setDueTodayItems([]);
        setWaitingItems(active.filter(t => isWithin(t.next_touch_at, bounds) || isWithin(t.dueAt, bounds)).filter(t => t.status === 'WAITING' || t.priority === 'WAITING_ON').map(mapTask));
        setScheduledItems(active.filter(t => isWithin(t.dueAt, bounds) && t.priority !== 'WAITING_ON' && t.status !== 'WAITING').map(mapTask));
        setCarryOverItems(active.filter(t => t.carry_over_from_date && isWithin(t.next_touch_at, bounds)).map(mapTask));
      } else {
        // Yesterday: closed-yesterday tasks + tasks that carried over FROM yesterday.
        const closedYesterday = tasks.filter(t => isWithin(t.closed_at, bounds));
        const carriedFromYesterday = tasks.filter(t => sameDateOnly(t.carry_over_from_date, bounds));
        setHandleNowItems([]);
        setDueTodayItems(closedYesterday.map(mapTask));
        setWaitingItems([]);
        setScheduledItems([]);
        setCarryOverItems(carriedFromYesterday.map(mapTask));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDemoData = async () => {
    setSeedingData(true);
    try {
      await seedData();
      toast.success('Showcase workspace loaded.');
      await loadData();
    } catch {
      toast.error('Could not load showcase workspace');
    } finally {
      setSeedingData(false);
    }
  };

  const handleStartShift = async () => {
    try {
      await startShift();
      setShiftStarted(true);
      toast.success('Shift started.', {
        description: 'Work top to bottom — urgent first.',
        action: {
          label: 'Take me to "Handle Now"',
          onClick: () => document.getElementById('handle-now')?.scrollIntoView({ behavior: 'smooth' })
        }
      });
    } catch {
      toast.error('Failed to start shift');
    }
  };

  const handleStartTask = async (sectionSetter, id, label = 'Taken — moved to In progress.') => {
    try {
      await updateTaskStatus(id, 'IN_PROGRESS');
      sectionSetter(prev => prev.map(item => item.id === id ? { ...item, status: 'In progress' } : item));
      toast.success(label);
    } catch {
      toast.error('Failed to start');
    }
  };

  const handleMarkDone = async (sectionSetter, id) => {
    try {
      await updateTaskStatus(id, 'DONE');
      sectionSetter(prev => prev.map(item => item.id === id ? { ...item, status: 'Done' } : item));
      toast.success('Marked done — logged in today\'s briefing.');
    } catch {
      toast.error('Failed to mark done');
    }
  };

  const OWNER_DISPLAY_LABELS = {
    front_desk: 'Front Desk', housekeeping: 'Housekeeping', maintenance: 'Maintenance',
    transport: 'Transport / Driver', concierge: 'Concierge', reservations: 'Reservations',
    manager_on_duty: 'Manager on Duty',
  };

  const handleAssign = async (sectionSetter, id, payload) => {
    // payload is { assigned_to_user_id, route_to } from AssignPopover
    const owner = payload?.assigned_to_user_id || 'front_desk';
    const routeTo = payload?.route_to || null;
    try {
      await setTaskAssignment(id, { assigned_to_user_id: owner, route_to: routeTo });
      sectionSetter(prev => prev.map(item =>
        item.id === id ? { ...item, owner, route_to: routeTo } : item
      ));
      toast.success(`Assigned to ${OWNER_DISPLAY_LABELS[owner] || owner}.`);
    } catch {
      toast.error('Could not save assignment.');
    }
  };

  // ---- Next-touch flow -----------------------------------------------------
  const openNextTouch = (item) => {
    // Pass the raw task fields the modal expects
    setNextTouchTask({
      id: item.id,
      title: item.title || item.summary,
      summary: item.summary,
      next_touch_at: item.next_touch_at || null,
      next_touch_reason: item.next_touch_reason || '',
      waiting_on: item.waitingOn || item.waiting_on || '',
    });
    setNextTouchOpen(true);
  };

  const handleConfirmNextTouch = async (payload) => {
    if (!nextTouchTask) return;
    try {
      await setTaskNextTouch(nextTouchTask.id, payload);
      toast.success('Next touch scheduled.');
      setNextTouchOpen(false);
      setNextTouchTask(null);
      // Reload so the task moves between sections if its status flipped to WAITING
      await loadData();
    } catch {
      toast.error('Could not schedule next touch.');
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp': return <MessageSquare className="h-3 w-3" />;
      case 'phone': return <Phone className="h-3 w-3" />;
      case 'email': return <Mail className="h-3 w-3" />;
      default: return <Inbox className="h-3 w-3" />;
    }
  };

  // Empty State Component
  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="text-center py-10 px-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 mb-4">
        <Icon className="h-6 w-6 text-stone-400" />
      </div>
      <p className="text-stone-700 font-medium">{title}</p>
      <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">{description}</p>
    </div>
  );

  const hasAnyData = handleNowItems.length > 0 || dueTodayItems.length > 0 || 
                     waitingItems.length > 0 || scheduledItems.length > 0 || carryOverItems.length > 0;

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-8 w-96" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="daily-run-page">
        {preventRepeatTask && (
          <PreventRepeatModal
            open={preventRepeatOpen}
            onOpenChange={setPreventRepeatOpen}
            taskId={preventRepeatTask.id}
            existingNote={preventRepeatTask.prevent_repeat_note}
            existingAction={preventRepeatTask.prevent_repeat_action}
            onSaved={() => loadData()}
          />
        )}
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900" data-testid="daily-run-title">
              Daily Run — {shiftDate === 'today' ? 'Today' : shiftDate === 'tomorrow' ? 'Tomorrow' : 'Yesterday'}
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              {shiftDate === 'today' && 'Your shift list — sorted by urgency across all properties.'}
              {shiftDate === 'tomorrow' && 'Looking ahead. Anything scheduled for tomorrow or carrying over with a touch tomorrow.'}
              {shiftDate === 'yesterday' && 'What you closed and what carried over from yesterday\u2019s shift.'}
            </p>
            <p className="text-xs text-stone-400 mt-0.5">
              {shiftDate === 'today' && 'Mark items done as you go. Anything unfinished gets a clean carry-over.'}
              {shiftDate === 'tomorrow' && 'Read-ahead view. Items here will arrive in tomorrow\u2019s shift.'}
              {shiftDate === 'yesterday' && 'Read-only audit view of yesterday\u2019s shift.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => setLogRequestOpen(true)} variant="outline" className="rounded-full" data-testid="log-request-btn">
              <Plus className="h-4 w-4 mr-2" /> Log request
            </Button>
            {/* View past closed concierge briefings (audit trail) */}
            <Button
              onClick={() => setReportsDrawerOpen(true)}
              variant="outline"
              className="rounded-full text-stone-600"
              data-testid="view-briefings-btn"
              title="View past closed concierge briefings"
            >
              <Eye className="h-4 w-4 mr-2" /> Past briefings
            </Button>
            {!shiftStarted ? (
              <Button 
                onClick={handleStartShift}
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                data-testid="start-shift-btn"
              >
                <Play className="h-4 w-4 mr-2" /> Start shift
              </Button>
            ) : (
              <Badge className="bg-green-100 text-green-700 px-3 py-1">
                <CheckCircle className="h-3 w-3 mr-1" /> Shift Active
              </Badge>
            )}
            {/* End shift is always available — clicking still opens carry-over modal even if no active shift */}
            <Button 
              variant="outline" 
              onClick={() => setEndShiftOpen(true)}
              className="rounded-full text-stone-600"
              data-testid="end-shift-btn"
            >
              <Moon className="h-4 w-4 mr-2" /> End shift
            </Button>
          </div>
        </div>

        {/* Shift banner */}
        {shiftStarted && (
          <div className="bg-[#3F617E]/5 border border-[#3F617E]/20 rounded-lg px-4 py-2 text-sm text-[#3F617E]" data-testid="shift-banner">
            Shift started. Work top to bottom — urgent first.
          </div>
        )}

        {/* Filters + Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-stone-100">
          <div className="flex flex-wrap items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="All Properties" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Priority view mixes properties so urgency wins.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Select value={shiftDate} onValueChange={setShiftDate}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="tomorrow">Tomorrow</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-2">
              <Switch 
                id="calm-mode" 
                checked={calmMode} 
                onCheckedChange={setCalmMode}
              />
              <Label htmlFor="calm-mode" className="text-sm text-stone-500">
                {calmMode ? (
                  <span className="flex items-center gap-1"><Moon className="h-3 w-3" /> Calm Mode</span>
                ) : (
                  <span className="flex items-center gap-1"><Sun className="h-3 w-3" /> Full View</span>
                )}
              </Label>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <p className="text-xs text-stone-400">Updated just now</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs" disabled={!hasAnyData}>
                    <FileDown className="h-3 w-3 mr-1" /> Export Shift Log
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Export works best after you've marked a few items complete.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Tip Line */}
        <p className="text-xs text-stone-400 -mt-2">
          Tip: Start with "Handle Now" — it keeps the day calm.
        </p>

        {!hasAnyData ? (
          /* Page-Level Empty State */
          <Card className="border-stone-200">
            <CardContent className="py-16 px-8">
              <div className="text-center max-w-lg mx-auto">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-stone-100 mb-6">
                  <Coffee className="h-8 w-8 text-stone-400" />
                </div>
                <h2 className="text-xl font-heading font-medium text-stone-800" data-testid="daily-run-empty-title">
                  {shiftDate === 'today' && 'Nothing on the run right now'}
                  {shiftDate === 'tomorrow' && 'Nothing scheduled for tomorrow yet.'}
                  {shiftDate === 'yesterday' && 'No shift activity recorded yesterday.'}
                </h2>
                <p className="text-stone-500 mt-3 leading-relaxed">
                  {shiftDate === 'today' && 'As messages come in and requests get tagged, your shift list will build itself — urgent items first, then today\u2019s work, then follow-ups.'}
                  {shiftDate === 'tomorrow' && 'Items will appear here as you schedule work or set next-touch times for tomorrow.'}
                  {shiftDate === 'yesterday' && 'Once an operator closes tasks or carries items over, yesterday\u2019s shift will show up here.'}
                </p>
                <div className="mt-6 flex flex-col items-center gap-3">
                  <div className="flex gap-3">
                    {shiftDate === 'today' && isAdmin && (
                      <Button 
                        onClick={handleLoadDemoData}
                        disabled={seedingData}
                        className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                        data-testid="dailyrun-load-showcase"
                      >
                        {seedingData ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Database className="h-4 w-4 mr-2" />
                        )}
                        Load Showcase
                      </Button>
                    )}
                    <Button variant="outline" className="rounded-full" onClick={loadData}>
                      <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                  </div>
                  {shiftDate === 'today' && isAdmin && (
                    <p className="text-xs text-stone-400">Showcase workspace previews the full flow with sandboxed sample records.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* SECTION 1: Handle Now (today only) */}
            {shiftDate === 'today' && (
            <Card className="border-stone-200 border-l-4 border-l-red-500" id="handle-now">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <CardTitle className="font-heading text-lg">Handle Now</CardTitle>
                </div>
                <CardDescription>Time-sensitive. Protects arrival, access, comfort, or trust.</CardDescription>
              </CardHeader>
              <CardContent>
                {handleNowItems.length > 0 ? (
                  <div className="space-y-3">
                    {handleNowItems.filter(i => i.status !== 'Done' || !calmMode).map(item => (
                      <TaskCard
                        key={item.id}
                        item={item}
                        variant="handle_now"
                        onHandle={() => handleStartTask(setHandleNowItems, item.id, 'Handling — moved to In progress.')}
                        onMarkDone={() => handleMarkDone(setHandleNowItems, item.id)}
                        onAssign={(owner) => handleAssign(setHandleNowItems, item.id, owner)}
                        onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState 
                    icon={CheckCircle}
                    title="No fires right now."
                    description="If something urgent comes in, it'll land here automatically."
                  />
                )}
              </CardContent>
            </Card>
            )}

            {/* SECTION 2: Due Today / Scheduled Tomorrow / Closed Yesterday */}
            <Card className="border-stone-200 border-l-4 border-l-amber-500">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  <CardTitle className="font-heading text-lg">
                    {shiftDate === 'today' && 'Due Today'}
                    {shiftDate === 'tomorrow' && 'Scheduled for Tomorrow'}
                    {shiftDate === 'yesterday' && 'Closed Yesterday'}
                  </CardTitle>
                </div>
                <CardDescription>
                  {shiftDate === 'today' && 'Items to close before end of day — even if they aren\u2019t urgent.'}
                  {shiftDate === 'tomorrow' && 'Items with a due time set for tomorrow.'}
                  {shiftDate === 'yesterday' && 'Tasks closed during yesterday\u2019s shift.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dueTodayItems.length > 0 ? (
                  <div className="space-y-3">
                    {dueTodayItems.filter(i => i.status !== 'Done' || !calmMode).map(item => (
                      <TaskCard
                        key={item.id}
                        item={item}
                        variant={shiftDate === 'today' ? 'due_today' : 'scheduled'}
                        onHandle={shiftDate === 'today' ? () => handleStartTask(setDueTodayItems, item.id, 'Taken — moved to In progress.') : undefined}
                        onMarkDone={shiftDate === 'today' ? () => handleMarkDone(setDueTodayItems, item.id) : undefined}
                        onAssign={shiftDate === 'today' ? (owner) => handleAssign(setDueTodayItems, item.id, owner) : undefined}
                        onSetNextTouch={shiftDate === 'today' ? () => openNextTouch(item) : undefined}
                        onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState 
                    icon={Calendar}
                    title={
                      shiftDate === 'today' ? 'Today looks clear.' :
                      shiftDate === 'tomorrow' ? 'Nothing scheduled for tomorrow yet.' :
                      'No shift activity recorded yesterday.'
                    }
                    description={
                      shiftDate === 'today' ? "When requests are tagged 'Today,' they'll show up here." :
                      shiftDate === 'tomorrow' ? 'Items with a due date tomorrow will appear here.' :
                      'Closed tasks from yesterday\u2019s shift will appear here.'
                    }
                  />
                )}
              </CardContent>
            </Card>

            {/* SECTION 3: Waiting On (not shown in yesterday view) */}
            {shiftDate !== 'yesterday' && (
            <Card className="border-stone-200 border-l-4 border-l-purple-500">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Pause className="h-5 w-5 text-purple-500" />
                  <CardTitle className="font-heading text-lg">Waiting On</CardTitle>
                </div>
                <CardDescription>Items paused because you need a reply, a vendor, or the property team.</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={waitingTab} onValueChange={setWaitingTab} className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="guest">Guest</TabsTrigger>
                    <TabsTrigger value="vendor">Vendor</TabsTrigger>
                    <TabsTrigger value="property">Property Team</TabsTrigger>
                  </TabsList>
                  
                  {['guest', 'vendor', 'property'].map(tab => {
                    const tabItems = waitingItems.filter(i => i.waitingOn === tab);
                    return (
                      <TabsContent key={tab} value={tab}>
                        {tabItems.length > 0 ? (
                          <div className="space-y-3">
                            {tabItems.map(item => (
                              <TaskCard
                                key={item.id}
                                item={item}
                                variant="waiting"
                                onMarkDone={() => handleMarkDone(setWaitingItems, item.id)}
                                onAssign={(owner) => handleAssign(setWaitingItems, item.id, owner)}
                                onSetNextTouch={() => openNextTouch(item)}
                                onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                              />
                            ))}
                          </div>
                        ) : (
                          <EmptyState 
                            icon={CheckCircle}
                            title="No replies needed right now."
                            description="Anything waiting for a reply lives here — not in your urgent lane."
                          />
                        )}
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </CardContent>
            </Card>
            )}

            {/* SECTION 4: Scheduled (not shown in yesterday view) */}
            {!calmMode && shiftDate !== 'yesterday' && (
              <Card className="border-stone-200 border-l-4 border-l-indigo-500">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-indigo-500" />
                    <CardTitle className="font-heading text-lg">Scheduled</CardTitle>
                  </div>
                  <CardDescription>Work that's planned — tomorrow morning, after check-in, after housekeeping, etc.</CardDescription>
                </CardHeader>
                <CardContent>
                  {scheduledItems.length > 0 ? (
                    <div className="space-y-3">
                      {scheduledItems.map(item => (
                        <TaskCard
                          key={item.id}
                          item={item}
                          variant="scheduled"
                          onPullIntoToday={() => toast.success('Pulled into today.')}
                          onSetNextTouch={() => openNextTouch(item)}
                          onAssign={(owner) => handleAssign(setScheduledItems, item.id, owner)}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState 
                      icon={CalendarClock}
                      title="Nothing scheduled yet."
                      description="If it can't be done today, schedule it — don't let it float."
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* SECTION 5: Carry-Over Review */}
            {carryOverItems.length > 0 && (
              <Card className="border-stone-200 border-l-4 border-l-stone-400" id="carry-over-review">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-stone-500" />
                    <CardTitle className="font-heading text-lg">Carry-Over Review</CardTitle>
                  </div>
                  <CardDescription>Each carry-over needs a next touch time. If it doesn't, it drifts.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {carryOverItems.map(item => (
                      <TaskCard
                        key={item.id}
                        item={item}
                        variant="carry_over"
                        onKeepActive={() => toast.success('Kept active.')}
                        onCloseOut={() => handleMarkDone(setCarryOverItems, item.id)}
                        onAssign={(owner) => handleAssign(setCarryOverItems, item.id, owner)}
                        onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Carry-Over Empty State */}
            {carryOverItems.length === 0 && !calmMode && (
              <Card className="border-stone-200 border-dashed">
                <CardContent className="p-6">
                  <EmptyState 
                    icon={CheckCircle}
                    title="Clean handoff."
                    description="Nothing carried over from yesterday — that's a good day."
                  />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Log Request Modal */}
        <LogRequestModal
          open={logRequestOpen}
          onOpenChange={setLogRequestOpen}
          onCreated={() => loadData()}
          defaultPropertyId={selectedProperty !== 'all' ? selectedProperty : ''}
        />
        
        {/* End Shift Modal */}
        <EndShiftModal
          open={endShiftOpen}
          onOpenChange={setEndShiftOpen}
          unfinishedTasks={allTasks.filter(t => t.status !== 'DONE' && !t.closed_at)}
          onShiftClosed={(res) => {
            setShiftStarted(false);
            loadData().then(() => {
              // Scroll to Carry-Over Review if there are carried-over items
              if (res?.updatedTasksCount > 0) {
                setTimeout(() => {
                  document.getElementById('carry-over-review')?.scrollIntoView({ behavior: 'smooth' });
                }, 250);
              }
            });
          }}
        />

        {/* Past Concierge Briefings (audit / read-only) */}
        <BriefingReportsDrawer open={reportsDrawerOpen} onOpenChange={setReportsDrawerOpen} />

        {/* Set next touch — adds the task to Today's Follow-ups */}
        <NextTouchModal
          open={nextTouchOpen}
          onOpenChange={setNextTouchOpen}
          task={nextTouchTask}
          onConfirm={handleConfirmNextTouch}
        />
      </div>
    </DeskLayout>
  );
};

export default DailyRun;
